<template>
    <v-container class="px-5">
        <v-row>
            <v-col>
                <h4>Pending Company Approvals</h4>
            </v-col>
        </v-row>

        <v-row>
            <v-col>
                <v-expansion-panels variant="accordion" class="mb-2">
                    <v-expansion-panel>
                        <v-expansion-panel-title>
                            <v-icon class="mr-2">mdi-email-outline</v-icon>
                            Notification Emails
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <p class="text-caption text-grey mb-2">
                                These email addresses will receive notifications when a new company signs up.
                            </p>
                            <div class="d-flex align-center ga-3">
                                <v-text-field v-model="notificationEmails" density="compact" variant="outlined"
                                              hide-details placeholder="admin@example.com, admin2@example.com"
                                              :loading="loadingEmails"/>
                                <v-btn color="primary" size="small" :loading="savingEmails"
                                       :disabled="!notificationEmails" @click="saveNotificationEmails">
                                    Save
                                </v-btn>
                            </div>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                </v-expansion-panels>
            </v-col>
        </v-row>

        <v-row>
            <v-col>
                <v-text-field v-model="search" density="compact" label="Search" prepend-inner-icon="mdi-magnify"
                              clearable variant="outlined" hide-details @update:model-value="debouncedFetch"/>
            </v-col>
        </v-row>

        <v-row>
            <v-col>
                <v-data-table-server v-model:items-per-page="perPage" v-model:page="page"
                                     :headers="headers" :items="companies" :items-length="totalItems"
                                     :loading="loading" @update:options="fetchCompanies">
                    <template v-slot:item.owner="{ item }">
                        <div v-if="item.owner">
                            {{ item.owner.first_name }} {{ item.owner.last_name }}
                            <div class="text-caption text-grey">{{ item.owner.email }}</div>
                        </div>
                    </template>
                    <template v-slot:item.created_at="{ item }">
                        {{ formatDate(item.created_at) }}
                    </template>
                    <template v-slot:item.actions="{ item }">
                        <v-btn icon size="x-small" variant="text" class="mr-1"
                               :loading="metadataLoadingId === item.id"
                               @click="showMetadata(item)">
                            <v-icon size="18">mdi-information-outline</v-icon>
                            <v-tooltip activator="parent" location="top">Registration Info</v-tooltip>
                        </v-btn>
                        <v-btn color="success" size="small" variant="tonal" class="mr-2"
                               :loading="approvingId === item.id" :disabled="approvingId !== null"
                               @click="approveCompany(item)">
                            <v-icon start>mdi-check</v-icon>
                            Approve
                        </v-btn>
                        <v-btn color="error" size="small" variant="tonal"
                               :disabled="approvingId !== null"
                               @click="openRejectDialog(item)">
                            <v-icon start>mdi-close</v-icon>
                            Reject
                        </v-btn>
                    </template>
                </v-data-table-server>
            </v-col>
        </v-row>

        <!-- Reject Dialog -->
        <v-dialog v-model="rejectDialog" max-width="500">
            <v-card>
                <v-card-title>Reject Company</v-card-title>
                <v-card-text>
                    <p class="mb-3">
                        Are you sure you want to reject <strong>{{ rejectTarget?.company_name }}</strong>?
                    </p>
                    <v-textarea v-model="rejectReason" label="Reason (optional)" rows="3" variant="outlined"
                                hint="This will be included in the rejection email" persistent-hint/>
                </v-card-text>
                <v-card-actions>
                    <v-spacer/>
                    <v-btn @click="rejectDialog = false">Cancel</v-btn>
                    <v-btn color="error" :loading="rejecting" @click="rejectCompany">Reject</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!-- Registration Metadata Dialog -->
        <v-dialog v-model="metadataDialog" max-width="650">
            <v-card>
                <v-card-title class="d-flex justify-space-between align-center">
                    <span>Registration Info - {{ metadataTarget?.company_name }}</span>
                    <v-btn icon size="small" variant="text" @click="metadataDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text>
                    <div v-if="!metadataItems.length" class="text-center py-4 text-grey">
                        No registration metadata recorded for this company.
                    </div>
                    <div v-for="(meta, i) in metadataItems" :key="i" class="mb-4">
                        <div class="d-flex align-center mb-2">
                            <v-chip size="x-small" variant="tonal" :color="meta.registration_step === 'company_created' ? 'primary' : meta.registration_step === 'user_created' ? 'info' : 'success'" class="mr-2">
                                {{ meta.registration_step }}
                            </v-chip>
                            <v-chip size="x-small" variant="tonal" :color="meta.client_source === 'web' ? 'blue' : 'green'" class="mr-2">
                                {{ meta.client_source || 'unknown' }}
                            </v-chip>
                            <span class="text-caption text-grey">{{ formatDate(meta.created_at) }}</span>
                        </div>
                        <v-table density="compact" class="border rounded">
                            <tbody>
                                <tr>
                                    <td class="font-weight-medium" width="140">IP Address</td>
                                    <td>{{ meta.ip_address || '-' }}</td>
                                </tr>
                                <tr v-if="meta.geo_country">
                                    <td class="font-weight-medium">Location</td>
                                    <td>
                                        {{ [meta.geo_city, meta.geo_region, meta.geo_country].filter(Boolean).join(', ') }}
                                        <v-chip v-if="meta.is_vpn" size="x-small" color="warning" variant="tonal" class="ml-2">VPN/Hosting</v-chip>
                                        <v-chip v-if="meta.is_proxy" size="x-small" color="error" variant="tonal" class="ml-1">Proxy</v-chip>
                                    </td>
                                </tr>
                                <tr v-if="meta.geo_isp">
                                    <td class="font-weight-medium">ISP</td>
                                    <td>{{ meta.geo_isp }}</td>
                                </tr>
                                <tr v-if="meta.geo_timezone">
                                    <td class="font-weight-medium">Timezone</td>
                                    <td>{{ meta.geo_timezone }}</td>
                                </tr>
                                <tr v-if="meta.browser_name">
                                    <td class="font-weight-medium">Browser</td>
                                    <td>{{ meta.browser_name }} {{ meta.browser_version }}</td>
                                </tr>
                                <tr v-if="meta.os_name">
                                    <td class="font-weight-medium">OS</td>
                                    <td>{{ meta.os_name }} {{ meta.os_version }}</td>
                                </tr>
                                <tr v-if="meta.device_model">
                                    <td class="font-weight-medium">Device</td>
                                    <td>{{ meta.device_model }}</td>
                                </tr>
                                <tr v-if="meta.screen_resolution">
                                    <td class="font-weight-medium">Screen</td>
                                    <td>{{ meta.screen_resolution }}</td>
                                </tr>
                                <tr v-if="meta.app_version">
                                    <td class="font-weight-medium">App Version</td>
                                    <td>{{ meta.app_version }}</td>
                                </tr>
                                <tr v-if="meta.user_agent">
                                    <td class="font-weight-medium">User Agent</td>
                                    <td class="text-caption" style="word-break: break-all;">{{ meta.user_agent }}</td>
                                </tr>
                                <tr v-if="meta.disposable_email">
                                    <td class="font-weight-medium">Email Risk</td>
                                    <td><v-chip size="x-small" color="error" variant="tonal">Disposable Email</v-chip></td>
                                </tr>
                            </tbody>
                        </v-table>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script setup>
import {onMounted, ref} from 'vue'
import {send} from '@/composables/apiClient'
import {useSnackbarStore} from '@/stores/snackBarStore'

const snackbar = useSnackbarStore()
const loading = ref(false)
const notificationEmails = ref('')
const loadingEmails = ref(false)
const savingEmails = ref(false)
const companies = ref([])
const totalItems = ref(0)
const page = ref(1)
const perPage = ref(15)
const search = ref('')

const rejectDialog = ref(false)
const rejectTarget = ref(null)
const rejectReason = ref('')
const rejecting = ref(false)
const approvingId = ref(null)
const metadataDialog = ref(false)
const metadataTarget = ref(null)
const metadataItems = ref([])
const metadataLoadingId = ref(null)

let debounceTimer = null

const headers = [
    {title: 'Company Name', key: 'company_name'},
    {title: 'Contact', key: 'owner', sortable: false},
    {title: 'Phone', key: 'phone'},
    {title: 'Industry', key: 'industry'},
    {title: 'Planned Assets', key: 'planned_asset_count'},
    {title: 'Registered', key: 'created_at'},
    {title: 'Actions', key: 'actions', sortable: false, align: 'end'},
]

const formatDate = (dateStr) => {
    if (!dateStr) return ''
    return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    })
}

const debouncedFetch = () => {
    clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => {
        page.value = 1
        fetchCompanies()
    }, 300)
}

const fetchCompanies = () => {
    loading.value = true
    send('get', `admin/pending-companies`, {
        page: page.value,
        per_page: perPage.value,
        search: search.value || undefined,
    }).then((response) => {
        const data = response.data.data
        companies.value = data.data
        totalItems.value = data.total
    }).catch((error) => {
        snackbar.showSnackbar(error.response?.data?.message || 'Failed to load pending companies', 'error')
    }).finally(() => loading.value = false)
}

const approveCompany = (company) => {
    approvingId.value = company.id
    send('put', `admin/companies/${company.id}/approve`)
        .then(() => {
            snackbar.showSnackbar(`${company.company_name} approved!`)
            fetchCompanies()
        }).catch(() => {
        snackbar.showSnackbar('Failed to approve company', 'error')
    }).finally(() => approvingId.value = null)
}

const openRejectDialog = (company) => {
    rejectTarget.value = company
    rejectReason.value = ''
    rejectDialog.value = true
}

const fetchNotificationEmails = () => {
    loadingEmails.value = true
    send('get', 'admin/approval-notification-emails')
        .then((response) => {
            notificationEmails.value = response.data.data.emails
        }).catch((error) => {
        snackbar.showSnackbar(error.response?.data?.message || 'Failed to load notification emails', 'error')
    }).finally(() => loadingEmails.value = false)
}

const saveNotificationEmails = () => {
    savingEmails.value = true
    send('put', 'admin/approval-notification-emails', {
        emails: notificationEmails.value,
    }).then((response) => {
        snackbar.showSnackbar(response.data.message)
    }).catch((error) => {
        snackbar.showSnackbar(error.response?.data?.message || 'Failed to save', 'error')
    }).finally(() => savingEmails.value = false)
}

const showMetadata = (company) => {
    metadataTarget.value = company
    metadataLoadingId.value = company.id
    const params = {company_id: company.id}
    if (company.owner?.id) params.user_id = company.owner.id
    send('get', 'admin/registration-metadata', params)
        .then((response) => {
            metadataItems.value = response.data.data || []
            metadataDialog.value = true
        }).catch(() => {
        snackbar.showSnackbar('Failed to load registration metadata', 'error')
    }).finally(() => metadataLoadingId.value = null)
}

onMounted(fetchNotificationEmails)

const rejectCompany = () => {
    rejecting.value = true
    send('put', `admin/companies/${rejectTarget.value.id}/reject`, {
        reason: rejectReason.value || undefined,
    }).then(() => {
        snackbar.showSnackbar(`${rejectTarget.value.company_name} rejected.`)
        rejectDialog.value = false
        fetchCompanies()
    }).catch(() => {
        snackbar.showSnackbar('Failed to reject company', 'error')
    }).finally(() => rejecting.value = false)
}
</script>
